import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../api';
import { format, formatDistanceToNow } from 'date-fns';

function fmtTime(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [att, setAtt] = useState(null);
  const [demos, setDemos] = useState([]);

  useEffect(() => {
    api.get('/tasks').then(r => setTasks(r.data));
    api.get('/attendance/my').then(r => setAtt(r.data));
    api.get('/demos/upcoming').then(r => setDemos(r.data));
  }, []);

  const pending = tasks.filter(t => t.status === 'pending').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const inProgress = tasks.filter(t => t.status === 'in_progress').length;
  const reqSec = (att?.requiredHoursPerDay || 8) * 3600;
  const todayPct = att ? Math.min(100, Math.round((att.todaySeconds / reqSec) * 100)) : 0;

  return (
    <div className="fadein">
      <div style={{ marginBottom: 22 }}>
        <div className="page-title">Dashboard</div>
        <div className="page-subtitle">
          {format(new Date(), 'EEEE, MMMM d, yyyy')} — Welcome back, {user?.name?.split(' ')[0]}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Tasks', value: tasks.length, color: 'var(--text-1)' },
          { label: 'Pending',     value: pending,      color: 'var(--amber)' },
          { label: 'In Progress', value: inProgress,   color: 'var(--accent)' },
          { label: 'Completed',   value: completed,    color: 'var(--green)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: att ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 16 }}>
        {/* Work hours */}
        {att && (
          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>Work Hours</div>
              <Link to="/attendance" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View →</Link>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>Today</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>{fmtTime(att.todaySeconds)} / {att.requiredHoursPerDay}h</span>
              </div>
              <div className="progress-bar" style={{ height: 6 }}>
                <div className="progress-fill" style={{ width: `${todayPct}%`, background: todayPct >= 100 ? 'var(--green)' : 'var(--accent)' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { label: 'This Week',  val: att.weekSeconds },
                { label: 'This Month', val: att.monthSeconds },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--surface-2)', borderRadius: 'var(--r)', padding: '10px 12px', border: '1px solid var(--border-light)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 3 }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>{fmtTime(s.val)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming demos */}
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>Upcoming Demos</div>
            <Link to="/demos" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View →</Link>
          </div>
          {demos.length === 0 ? (
            <div className="empty" style={{ padding: '24px 0' }}>
              <div className="empty-icon"></div>
              <p>No upcoming demos</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {demos.slice(0, 3).map(d => (
                <div key={d._id} style={{ display: 'flex', gap: 10, padding: 10, background: 'var(--surface-2)', borderRadius: 'var(--r)', border: '1px solid var(--border-light)' }}>
                  <div style={{ width: 36, height: 36, background: 'var(--accent-dim)', borderRadius: 'var(--r-sm)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', lineHeight: 1 }}>{format(new Date(d.scheduledDemoAt), 'dd')}</div>
                    <div style={{ fontSize: 9, color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase' }}>{format(new Date(d.scheduledDemoAt), 'MMM')}</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)' }}>{d.lead?.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
                      {format(new Date(d.scheduledDemoAt), 'hh:mm a')} · {formatDistanceToNow(new Date(d.scheduledDemoAt), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent tasks */}
      <div className="card">
        <div className="card-header">
          <span className="card-header-title">Recent Tasks</span>
          <Link to="/tasks" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>View all →</Link>
        </div>
        {tasks.length === 0 ? (
          <div className="empty"><div className="empty-icon"></div><p>No tasks assigned</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Due</th>
                {user?.role === 'superadmin' && <th>Assigned To</th>}
              </tr>
            </thead>
            <tbody>
              {tasks.slice(0, 6).map(t => (
                <tr key={t._id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{t.title}</div>
                    {t.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{t.description}</div>}
                  </td>
                  <td><PriorityTag p={t.priority} /></td>
                  <td><span className={`badge badge-${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{t.dueDate ? format(new Date(t.dueDate), 'MMM d') : '—'}</td>
                  {user?.role === 'superadmin' && <td style={{ fontSize: 13 }}>{t.assignedTo?.name}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PriorityTag({ p }) {
  const c = { high: 'var(--red)', medium: 'var(--amber)', low: 'var(--green)' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: c[p] || c.medium }}>{p}</span>;
}
